import { useEffect, useState } from "react";
import { AbsoluteFill, useCurrentFrame, useVideoConfig } from "remotion";
import {
  extractVideoFrameUrl,
  settledVideoFrameUrl,
} from "../extract-video-frame";
import { mediaTimestampSeconds } from "../media-timestamp";
import type { LayerTransform } from "../types";
import { videoContentStyle } from "./video-crop-style";

interface VideoStillLayerProps {
  src: string;
  playbackRate?: number;
  content?: LayerTransform;
}

/**
 * Renders a single decoded frame of a video layer, for surfaces that display
 * one frame and never play (Remotion's <Thumbnail>).
 *
 * Mounting the real <Video> there works but is priced for playback: its
 * preview MediaPlayer primes iterators and buffers ahead, which on an
 * unbounded `bytes=N-` range pulls most of the file to paint one still.
 * Seeking a VideoSampleSink instead reads the header, the moov atom, and the
 * one GOP it needs.
 *
 * The frame follows the composition, so a trimmed clip shows its real in-point
 * and a cropped clip is cropped by the same `videoContentStyle` the playing
 * layer uses.
 *
 * Lambda exports must continue to use OffthreadVideoLayer for actual frames.
 */
export function VideoStillLayer({
  src,
  playbackRate,
  content,
}: VideoStillLayerProps) {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const timestampInSeconds = mediaTimestampSeconds({
    frame,
    fps,
    playbackRate,
  });

  // Seeded from the cache so a remount paints the frame immediately rather
  // than flashing empty while the same frame is extracted again
  // (`briefs/brief/index.tsx` renders its thumbnail rail from several tree
  // positions, so loading-to-loaded swaps really do remount these).
  const [url, setUrl] = useState(() =>
    settledVideoFrameUrl(src, timestampInSeconds),
  );

  useEffect(() => {
    const cached = settledVideoFrameUrl(src, timestampInSeconds);
    if (cached) {
      setUrl(cached);
      return;
    }

    let canceled = false;
    extractVideoFrameUrl({ src, timestampInSeconds })
      .then((extracted) => {
        if (!canceled) setUrl(extracted);
      })
      .catch((err: unknown) => {
        // Best-effort: the layer stays blank and the rest of the still renders.
        console.warn("[ad-studio] video still extraction failed", src, err);
      });

    return () => {
      canceled = true;
    };
  }, [src, timestampInSeconds]);

  return (
    <AbsoluteFill>
      {url ? <img src={url} alt="" style={videoContentStyle(content)} /> : null}
    </AbsoluteFill>
  );
}
